import { useCallback, useMemo, useState } from 'react'

/**
 * Which appointment's detail preview is open, for one calendar view.
 *
 * Click-driven: clicking a card opens its preview, clicking the same card again
 * closes it, clicking another card switches to that one, and clicking outside
 * closes (Mantine's own click-outside on the popover). State lives in the view
 * rather than in each strip so only one preview can be open at a time.
 *
 * **Only an appointment still on the calendar can be open.** Cancelling, transferring or
 * reassigning removes the strip without a click to close its preview, and a stale `openId` left
 * every remaining card dimmed as though another one were open.
 */
export function usePreviewPopover(events: readonly { id: string }[]) {
  const [selectedId, setOpenId] = useState<string | null>(null)

  const open = useCallback((id: string) => setOpenId(id), [])
  const close = useCallback(() => setOpenId(null), [])
  const openId = useMemo(
    () => (selectedId && events.some((event) => event.id === selectedId) ? selectedId : null),
    [selectedId, events],
  )

  return { openId, open, close }
}

/** State + handlers returned by {@link usePreviewPopover}, passed to event strips. */
export type PreviewPopover = ReturnType<typeof usePreviewPopover>
